import { useMemo } from 'react';

import { useParams } from 'react-router';

import { Checkbox, FormControlLabel, Grid } from '@mui/material';
import { Field, FormikProps } from 'formik';
import * as Yup from 'yup';

import { CissPrimaryKeyField, CissTextField } from 'components/fields';
import { Row } from 'components/form';
import { Block, FormPage } from 'components/page';
import { useFormContext } from 'context/FormContext';
import { useLoaderContext } from 'context/LoaderContext';
import { usePermissionsContext } from 'context/PermissionsContext';
import { PermissoesModulosPanel, PermissoesUsuariosPanel } from 'pages/cadastro/permissoes/components';
import { useMutatePermissoes, useQueryModulos } from 'pages/cadastro/permissoes/hooks';
import { PermissoesProps } from 'pages/cadastro/permissoes/types';

export function PermissoesFormComp() {
    const { content } = useFormContext();
    const { id } = useParams();
    const { hasPermission } = usePermissionsContext();
    const { onSubmit } = useMutatePermissoes();
    const { data: permissionsModules, isFetching } = useQueryModulos();

    useLoaderContext(id ? undefined : isFetching);

    const validationSchema = useMemo(
        () =>
            Yup.object({
                idGrupoUsuario: Yup.number(),
                dsGrupoUsuario: Yup.string().required(),
                fgAtivo: Yup.boolean(),
                fgPadraoFranqueado: Yup.boolean().optional(),
                dsDescricao: Yup.string().optional(),
            }),
        [],
    );

    const getInitialValues = useMemo(
        () => ({
            idGrupoUsuario: 0,
            dsGrupoUsuario: '',
            dsObservacao: '',
            fgPadraoFranqueado: false,
            fgAtivo: true,
            grupoUsuarioRecursoPermissoes: id ? [] : permissionsModules || [],
            usuarios: id && content?.usuarios ? content.usuarios : [],
        }),
        [id, permissionsModules, content],
    );

    return (
        <FormPage
            title="Grupo de Permissões"
            values={getInitialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
            validateOnSubmit={true}
            validateOnChange={true}
            url="/gestao/grupousuario"
            fgAtivo={hasPermission(['GRUPO_PERMISSAO_MODERAR'])}
        >
            {(formik: FormikProps<Partial<PermissoesProps>>) => (
                <>
                    <Block>
                        <Row>
                            <Grid>
                                <CissPrimaryKeyField
                                    value={formik.values.idGrupoUsuario}
                                    name="idGrupoUsuario"
                                    sx={{ width: { xs: '100%', lg: 100 } }}
                                />
                            </Grid>

                            <Grid sx={{ flex: 1 }}>
                                <CissTextField
                                    label="Nome do grupo de permissões"
                                    name="dsGrupoUsuario"
                                    value={formik.values.dsGrupoUsuario}
                                    onChange={(e) => formik.setFieldValue('dsGrupoUsuario', e.target.value)}
                                    error={formik.errors.dsGrupoUsuario}
                                />
                            </Grid>

                            <Grid sx={{ alignItems: 'center', display: 'flex' }}>
                                <Field as={FormControlLabel} type="checkbox" name="fgPadraoFranqueado" control={<Checkbox />} label="Padrão" />
                            </Grid>
                        </Row>

                        <Row sx={{ mb: 0 }}>
                            <Grid sx={{ flex: 1 }}>
                                <CissTextField
                                    label="Observação"
                                    name="dsObservacao"
                                    multiline
                                    rows={4}
                                    value={formik.values.dsObservacao}
                                    error={formik.errors.dsObservacao}
                                    onChange={(e) => formik.setFieldValue('dsObservacao', e.target.value)}
                                />
                            </Grid>
                        </Row>
                    </Block>

                    <PermissoesModulosPanel />

                    <PermissoesUsuariosPanel />
                </>
            )}
        </FormPage>
    );
}
